'use client';
import React, { useState, useEffect } from 'react';
import LoginForm from './LoginForm';
import SignupStep1 from './SignupStep1';
import SignupStep2 from './SignupStep2';
import AppLogo from '@/components/ui/AppLogo';

type AuthMode = 'login' | 'signup-step1' | 'signup-step2';

const greetings = [
  { text: 'As-salamu alaykum', lang: 'English' },
  { text: 'السلام عليكم', lang: 'Arabic' },
  { text: 'Assalaamu Calaykum', lang: 'Somali' },
  { text: 'السلام علیکم', lang: 'Urdu' },
  { text: 'Assalamu\'alaikum', lang: 'Indonesian' },
];

export interface SignupData {
  name: string;
  email: string;
  password: string;
  language: string;
  timezone: string;
  wakeTime: string;
  sleepTime: string;
}

export default function AuthPageClient() {
  const [mode, setMode] = useState<AuthMode>('login');
  const [greetingIndex, setGreetingIndex] = useState(0);
  const [signupData, setSignupData] = useState<Partial<SignupData>>({});
  const [referralCode, setReferralCode] = useState<string | null>(null);

  // Capture referral code from URL ?ref= param and persist in sessionStorage
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const refCode = params.get('ref');
    if (refCode) {
      sessionStorage.setItem('referral_code', refCode.toUpperCase());
      setReferralCode(refCode.toUpperCase());
    } else {
      const stored = sessionStorage.getItem('referral_code');
      if (stored) setReferralCode(stored);
    }
  }, []);

  React.useEffect(() => {
    const interval = setInterval(() => {
      setGreetingIndex((prev) => (prev + 1) % greetings.length);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  const handleStep1Complete = (data: Pick<SignupData, 'name' | 'email' | 'password'>) => {
    setSignupData((prev) => ({ ...prev, ...data }));
    setMode('signup-step2');
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Brand Panel */}
      <div className="hidden lg:flex lg:w-1/2 prayer-card-bg flex-col justify-between p-12 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-white/5 -translate-y-32 translate-x-32" />
        <div className="absolute bottom-0 left-0 w-80 h-80 rounded-full bg-white/5 translate-y-24 -translate-x-24" />
        <div className="absolute top-1/3 right-1/4 w-48 h-48 rounded-full bg-white/3" />

        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-2">
            <AppLogo size={44} />
            <span className="text-2xl font-800 text-white">Rafeeq</span>
          </div>
          <p className="text-white/60 text-sm">رفيق · Your Daily Companion</p>
        </div>

        <div className="relative z-10 flex-1 flex flex-col justify-center">
          {/* Rotating greeting */}
          <div className="mb-8">
            <div
              key={`greeting-${greetingIndex}`}
              className="slide-up"
            >
              <p
                className="text-5xl font-800 text-white mb-2 leading-tight"
                dir={greetings[greetingIndex].lang === 'Arabic' || greetings[greetingIndex].lang === 'Urdu' ? 'rtl' : 'ltr'}
              >
                {greetings[greetingIndex].text}
              </p>
              <p className="text-white/60 text-sm">{greetings[greetingIndex].lang}</p>
            </div>
          </div>

          <div className="space-y-4">
            {[
              { emoji: '🎯', title: 'Daily Wins Extracted', desc: 'AI finds positives even in your hardest days' },
              { emoji: '🎬', title: 'Personalized Video Compilations', desc: 'Custom motivational videos built just for you' },
              { emoji: '🕌', title: 'Prayer Times & Streaks', desc: 'Stay connected to your faith and your growth' },
              { emoji: '🗣️', title: 'Voice Coaching in 5 Languages', desc: 'Rafeeq speaks your language, literally' },
            ].map((feature) => (
              <div key={`feature-${feature.emoji}`} className="flex items-start gap-3">
                <span className="text-2xl flex-shrink-0">{feature.emoji}</span>
                <div>
                  <p className="text-white font-600 text-sm">{feature.title}</p>
                  <p className="text-white/60 text-xs">{feature.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10">
          <p className="text-white/40 text-xs">
            Trusted by thousands of Muslims worldwide for daily growth
          </p>
        </div>
      </div>

      {/* Right Form Panel */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center bg-background px-6 sm:px-12 lg:px-16 xl:px-20 py-12 overflow-y-auto">
        {/* Mobile logo */}
        <div className="flex lg:hidden items-center gap-2 mb-8">
          <AppLogo size={36} />
          <span className="text-xl font-800 text-foreground">Rafeeq</span>
        </div>

        {/* Referral code banner */}
        {referralCode && (mode === 'signup-step1' || mode === 'signup-step2') && (
          <div className="mb-5 flex items-center gap-2 bg-primary/8 border border-primary/20 rounded-xl px-4 py-3">
            <span className="text-base">🎁</span>
            <p className="text-sm text-primary/90">
              Referred by <span className="font-700">{referralCode}</span> — your discount will be applied at checkout.
            </p>
          </div>
        )}

        {mode === 'login' && (
          <LoginForm onSwitchToSignup={() => setMode('signup-step1')} />
        )}
        {mode === 'signup-step1' && (
          <SignupStep1
            onNext={handleStep1Complete}
            onSwitchToLogin={() => setMode('login')}
          />
        )}
        {mode === 'signup-step2' && (
          <SignupStep2
            signupData={signupData}
            referralCode={referralCode}
            onBack={() => setMode('signup-step1')}
          />
        )}
      </div>
    </div>
  );
}